import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { monitoringExceptionHelper } from 'instrumentation/helpers';
import { useToast } from '__components/ToastProvider';
import { toastVariant } from '__components/ToastProvider';
import { SetupAssistantRow } from './inputs/Row';
import { SetupAssistantModal } from './Modal';
import Loader from '__components/Kizen/Loader';
import { InlineSetupViewWrapper, ModalWrapper } from './styles';
import { InlineSave } from './InlineSave';
import { MarketplacePlugin } from '__pages/Marketplace/types';
import PluginService from 'services/PluginService';
import { useQuery } from '@tanstack/react-query';
import { PLUGINS } from '__queries/query-keys';
import { useInvalidateIntegrationSecrets } from 'query/integration-secrets';
import { Typography } from '@kizen/kds/Typography';
import { Spacer } from '@kizen/kds/Spacer';
import { InlineImport } from './InlineImport';
import { usePluginKZNImporter } from '../Importer/usePluginKZNImporter';
import { ImportProgressStep } from '../Importer/ImportProgressStep';
import { ActionMapping } from './ActionMapping';
import { OAuthStep } from './OAuthStep';
import { BusinessPluginApp } from 'ts-components/Plugins/types';
import { PluginContext } from 'ts-components/Plugins/context';
import { useMarketplacePermissions } from '__pages/Marketplace/permissions';
import { SetupAssistantController } from '@kizenapps/engine/react';
import CustomObjectsService from '__services/CustomObjectsService';
import { AssistantField } from '@kizenapps/engine';
import {
  getSetupViewApiName,
  SetupLevel,
  SetupView,
  SetupViewModal,
} from './SetupView';
import {
  useApiKeySecretsSeed,
  withApiKeyFieldsUnrequired,
} from '__pages/Marketplace/Details/secrets';

type AssistantProps = {
  config: MarketplacePlugin;
  onClose?: () => void;
  onChangeConfig?: (
    pluginApiName: string,
    config: Record<string, any>,
    templateAssociations?: Array<any>
  ) => Promise<unknown>;
  businessPluginAppsByApiName: Record<string, any>;
  inline?: boolean;
  onImportKZNFileStep?: (pluginAPIName: string, etag?: string) => Promise<void>;
  setupLevel?: SetupLevel;
};

export const SetupAssistant = ({
  config,
  onClose,
  onChangeConfig,
  businessPluginAppsByApiName,
  inline,
  onImportKZNFileStep,
  setupLevel = 'business',
}: AssistantProps) => {
  const [saving, setSaving] = useState(false);
  // Bumped when the user continues past the import progress, to remount the controller so
  // its one-time object inference runs against the freshly imported objects
  const [controllerKey, setControllerKey] = useState(0);
  const [showToast] = useToast();
  const { t } = useTranslation();
  const invalidateIntegrationSecrets = useInvalidateIntegrationSecrets();

  // Bumped after saving the config, to remount the controller
  const [remountKey, setRemountKey] = useState(0);

  const {
    canEditPlugins,
    canEditIntegrationSecrets,
    canViewIntegrationSecrets,
  } = useMarketplacePermissions();

  const { data: templateAssociations } = useQuery({
    queryFn: () =>
      PluginService.getTemplateAssociations(
        businessPluginAppsByApiName[config.api_name]?.id
      ),
    queryKey: PLUGINS.GET_JS_ACTION_TEMPLATE_ASSOCIATIONS(
      businessPluginAppsByApiName[config.api_name]?.id
    ),
    enabled:
      Boolean(businessPluginAppsByApiName[config.api_name]?.id) &&
      canEditPlugins,
  });

  const templateAssociationsByActionApiName = useMemo(() => {
    return templateAssociations?.reduce(
      (acc: any, curr: Record<string, any>) => {
        const actionApiName = curr.browser_js_action_template?.api_name;

        if (!actionApiName) {
          return acc;
        }

        if (!acc[actionApiName]) {
          acc[actionApiName] = [];
        }

        acc[actionApiName].push(curr);

        return acc;
      },
      {}
    );
  }, [templateAssociations]);

  const handleClose = useCallback(
    async (state?: Record<string, any>) => {
      if (state) {
        try {
          setSaving(true);

          await onChangeConfig?.(config.api_name, state, templateAssociations);

          invalidateIntegrationSecrets();
          setRemountKey((k) => k + 1);

          showToast({
            message: t('Configuration saved successfully'),
            variant: toastVariant.SUCCESS,
          });
        } catch (ex: any) {
          showToast({
            message: ex?.message || t('Failed to save configuration'),
            variant: toastVariant.FAILURE,
          });

          monitoringExceptionHelper(ex);
        } finally {
          setSaving(false);
        }
      }

      onClose?.();
    },
    [
      onClose,
      config.api_name,
      onChangeConfig,
      showToast,
      t,
      templateAssociations,
      invalidateIntegrationSecrets,
    ]
  );

  const businessPluginApp: BusinessPluginApp = useMemo(() => {
    return businessPluginAppsByApiName[config.api_name];
  }, [businessPluginAppsByApiName, config.api_name]);

  const existingConfig = businessPluginApp?.config;

  const { seededValues, isLoading: secretsLoading } = useApiKeySecretsSeed(
    config.api_name,
    config.base_config?.setup_assistant,
    existingConfig?.__kizen_setup_assistant_values,
    canViewIntegrationSecrets || canEditIntegrationSecrets
  );

  const { plan, entitlements } = useContext(PluginContext);

  const setupAssistantConfig = useMemo(() => {
    const raw = config.base_config?.setup_assistant;

    if (!raw) {
      return raw;
    }

    return {
      ...raw,
      fields: withApiKeyFieldsUnrequired(raw.fields, canEditIntegrationSecrets),
    };
  }, [config.base_config?.setup_assistant, canEditIntegrationSecrets]);

  const oAuthServices = useMemo(() => {
    return (
      businessPluginApp?.plugin_app?.services?.filter(
        (s) => s.auth_type === 'oauth'
      ) ?? []
    );
  }, [businessPluginApp]);

  const hasOauthServices = Boolean(oAuthServices.length > 0);

  const businessPluginAppId = businessPluginApp?.id ?? '';

  const { data: pluginAppEvents, refetch: refetchPluginAppEvents } = useQuery({
    // Fetching events has fallback logic for empty IDs, so it's safe to call even if the ID is an empty string
    queryFn: () => PluginService.getEvents(businessPluginAppId),
    queryKey: PLUGINS.GET_EVENTS(businessPluginAppId),
    // Always refetch on focus because we need this state to be up to date when returning to the tab after authorizing
    refetchOnWindowFocus: 'always',
    // We poll for new events if there are OAuth services or if a KZN file has ever been imported, since both of these can have external changes
    // that need to show in relatively real-time
    refetchInterval: hasOauthServices ? 15000 : false,
  });

  const oAuthEvents = useMemo(() => {
    return (
      pluginAppEvents?.events.filter((e: any) =>
        e.resource_path.includes('oauth')
      ) ?? []
    );
  }, [pluginAppEvents]);

  const prerequisiteOAuthServices = useMemo(() => {
    return oAuthServices.filter((oAuthService) => {
      const matchingItem = config.base_config?.setup_assistant?.services?.find(
        (s) => s.api_name === oAuthService.service_name
      );

      return matchingItem?.prerequisite;
    });
  }, [oAuthServices, config]);

  const requiredOAuthServices = useMemo(() => {
    return prerequisiteOAuthServices.filter((oAuthService) => {
      const matchingItem = config.base_config?.setup_assistant?.services?.find(
        (s) => s.api_name === oAuthService.service_name
      );

      return matchingItem?.required;
    });
  }, [prerequisiteOAuthServices, config]);

  const requiredOAuthEvents = useMemo(() => {
    return oAuthEvents.filter((event: any) => {
      const match = requiredOAuthServices.find(
        (e) => e.service_name === event.context?.service
      );
      return Boolean(match);
    });
  }, [oAuthEvents, requiredOAuthServices]);

  const optionalOAuthEvents = useMemo(() => {
    return oAuthEvents.filter((event: any) => {
      const match = prerequisiteOAuthServices.find(
        (e) => e.service_name === event.context?.service
      );
      return Boolean(match);
    });
  }, [oAuthEvents, prerequisiteOAuthServices]);

  const requiredOAuthErrors = useMemo(() => {
    return requiredOAuthEvents.filter(
      (event: any) => event.event_type === 'error'
    );
  }, [requiredOAuthEvents]);

  const optionalOAuthErrors = useMemo(() => {
    return optionalOAuthEvents.filter(
      (event: any) => event.event_type === 'error'
    );
  }, [optionalOAuthEvents]);

  const requiredAuthNeeded = requiredOAuthErrors.length > 0;

  const dynamicFieldAuthNeeded = optionalOAuthErrors.length > 0;

  const hasActions =
    (config.base_config?.setup_assistant?.actions?.length ?? 0) > 0;

  const hasFields =
    (config.base_config?.setup_assistant?.fields?.length ?? 0) > 0;

  const {
    onImport,
    onSkip,
    isImportWizardMode,
    pending: kznActionPending,
    showBlockingImportOnEnable,
    importProgress,
    onContinueAfterImport,
    refreshingObjects,
  } = usePluginKZNImporter({
    config,
    businessPluginAppsByApiName,
    onImportKZNFileStep,
  });

  const handleContinueAfterImport = () =>
    onContinueAfterImport(() => setControllerKey((key) => key + 1));

  const continueImportDisabled =
    importProgress?.phase === 'polling' || refreshingObjects;

  const showImportStep = showBlockingImportOnEnable || importProgress !== null;
  const showInlineImportStep = isImportWizardMode || importProgress !== null;

  const setupViewApiName = getSetupViewApiName(config);

  if (setupViewApiName && !showInlineImportStep && !showImportStep) {
    const setupView = (
      <SetupView
        pluginApiName={config.api_name}
        viewApiName={setupViewApiName}
        level={setupLevel}
        onComplete={onClose}
        inline={inline}
      />
    );

    if (inline) {
      return <InlineSetupViewWrapper>{setupView}</InlineSetupViewWrapper>;
    }

    return <SetupViewModal onClose={onClose}>{setupView}</SetupViewModal>;
  }

  if (inline) {
    if (!showInlineImportStep && !config.base_config?.setup_assistant) {
      onClose?.();
    }

    if (secretsLoading) {
      return <Loader loading />;
    }

    return (
      <SetupAssistantController
        key={`${remountKey}-${controllerKey}`}
        config={setupAssistantConfig ?? { fields: [] }}
        value={seededValues}
        templateAssociationsByActionApiName={
          templateAssociationsByActionApiName
        }
        disabledKeys={config.base_config?.disabled_keys}
        getObjectByAPIName={CustomObjectsService.getObjectByAPIName}
        getCustomObjectDetails={CustomObjectsService.getCustomObjectDetails}
        entitlements={entitlements}
        plan={plan}
      >
        <ModalWrapper className="relative">
          {showInlineImportStep ? (
            <div>
              {importProgress ? (
                <ImportProgressStep
                  importProgress={importProgress}
                  onContinue={handleContinueAfterImport}
                  continueDisabled={continueImportDisabled}
                />
              ) : (
                <InlineImport
                  pending={kznActionPending}
                  onImport={onImport}
                  onSkip={onSkip}
                />
              )}
            </div>
          ) : (
            <>
              {prerequisiteOAuthServices.length > 0 ? (
                <>
                  <OAuthStep
                    oAuthServices={prerequisiteOAuthServices}
                    oAuthEvents={oAuthEvents}
                    refetch={refetchPluginAppEvents}
                    businessPluginApp={businessPluginApp}
                  />
                  <Spacer size={10} />
                </>
              ) : null}

              {hasActions ? (
                <ActionMapping config={config} disabled={requiredAuthNeeded} />
              ) : null}
              {hasActions && hasFields ? <Spacer size={10} /> : null}
              {hasFields ? (
                <>
                  <Typography variant="header" size="xl" weight="semibold">
                    {t('Settings')}
                  </Typography>
                  {/* Typography trims its own leading, so the flex gap alone reads as
                      cramped under a heading. */}
                  <Spacer size={10} />
                  {setupAssistantConfig?.fields?.map((field) => {
                    return (
                      <SetupAssistantRow
                        key={field.key}
                        field={field as AssistantField}
                        pluginApiName={config.api_name}
                        disabled={requiredAuthNeeded}
                        disableDynamicFields={dynamicFieldAuthNeeded}
                      />
                    );
                  })}
                </>
              ) : null}

              <InlineSave disabled={saving} onClick={handleClose} />
            </>
          )}
        </ModalWrapper>
      </SetupAssistantController>
    );
  }

  if (!showImportStep && !config.base_config?.setup_assistant) {
    return null;
  }

  if (secretsLoading) {
    return <Loader loading />;
  }

  return (
    <SetupAssistantController
      key={`${remountKey}-${controllerKey}`}
      config={setupAssistantConfig ?? { fields: [] }}
      value={seededValues}
      templateAssociationsByActionApiName={templateAssociationsByActionApiName}
      disabledKeys={config.base_config?.disabled_keys}
      getObjectByAPIName={CustomObjectsService.getObjectByAPIName}
      getCustomObjectDetails={CustomObjectsService.getCustomObjectDetails}
      entitlements={entitlements}
      plan={plan}
    >
      <SetupAssistantModal
        onClose={
          showBlockingImportOnEnable && !importProgress ? onSkip : handleClose
        }
        config={config}
        disabled={saving}
        schemaImportStep={showImportStep}
        onContinueImport={
          importProgress ? handleContinueAfterImport : undefined
        }
        continueImportDisabled={continueImportDisabled}
      >
        <ModalWrapper>
          {showImportStep ? (
            <div>
              {importProgress ? (
                <>
                  <ImportProgressStep
                    importProgress={importProgress}
                    showEmail
                  />
                  <Spacer size={5} />
                </>
              ) : (
                <InlineImport
                  pending={kznActionPending}
                  onImport={onImport}
                  onSkip={onSkip}
                  inModal
                />
              )}
            </div>
          ) : (
            <>
              <OAuthStep
                oAuthServices={requiredOAuthServices}
                oAuthEvents={oAuthEvents}
                refetch={refetchPluginAppEvents}
                businessPluginApp={businessPluginApp}
              />
              {hasActions ? (
                <ActionMapping config={config} disabled={requiredAuthNeeded} />
              ) : null}
              {hasActions && hasFields ? <Spacer size={15} /> : null}
              {hasFields ? (
                <>
                  {hasActions ? (
                    <>
                      <Typography variant="header" size="lg" weight="semibold">
                        {t('Settings')}
                      </Typography>
                      {/* Typography trims its own leading, so the flex gap alone reads as
                          cramped under a heading. */}
                      <Spacer size={10} />
                    </>
                  ) : null}
                  {setupAssistantConfig?.fields?.map((field) => {
                    return (
                      <SetupAssistantRow
                        key={field.key}
                        field={field as AssistantField}
                        pluginApiName={config.api_name}
                        disabled={requiredAuthNeeded}
                        disableDynamicFields={dynamicFieldAuthNeeded}
                      />
                    );
                  })}
                </>
              ) : null}
            </>
          )}
        </ModalWrapper>
      </SetupAssistantModal>
    </SetupAssistantController>
  );
};
